<template>
  <b-container fluid>
    <p>&nbsp;</p>
    <b-row>
      <b-col>
        <b-button variant="primary" @click="onOpen">
          <fa-icon icon="plus"></fa-icon>
          新增模型
        </b-button>
        <b-btn
          @click="table.onlyMe = !table.onlyMe"
          variant="info">
          <fa-icon icon="globe-americas" v-if="table.onlyMe"></fa-icon>
          <fa-icon icon="user" v-if="!table.onlyMe"></fa-icon>
          {{ table.onlyMe ? '顯示全部模型' : '只顯示我的模型' }}
        </b-btn>
        <div class="d-md-none mt-3"></div>
        <span class="text-secondary" v-if="status.refreshing">
          更新資料中 
          <fa-icon icon="spinner" spin></fa-icon>
        </span>
      </b-col>
      <b-col cols="12" md="3" align-self="end">
        <div class="d-md-none mt-3"></div>
        <b-form-group>
          <b-input-group>
            <b-form-input v-model="table.filter" placeholder="搜尋..." />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-table
        show-empty
        stacked="md"
        :items="tableItem"
        :fields="table.fields"
        :current-page="table.currentPage"
        :per-page="table.perPage"
        :filter="table.filter"
        :empty-text="constant.strings.emptyText"
        :empty-filtered-text="constant.strings.emptyFilteredText"
        @filtered="onFiltered"
      >
        <template slot="classCount" slot-scope="data">
          {{ data.item.classes.length }}
        </template>
        <template slot="progress" slot-scope="data">
          {{ getModelStatus(data.item) }}
          <a :href="getCurrentHash" :id="`detail-${data.item.id}`">詳情</a>
            <b-popover
              triggers="hover focus"
              title="模型詳情"
              placement="auto"
              :target="`detail-${data.item.id}`">
              <span class="text-secondary">Learning Rate</span>
              <h2 class="thin-title">{{ Number(data.item.hyperParameters.learningRate) || 'N/A' }}</h2>
              <span class="text-secondary">Batch Size Fraction</span>
              <h2 class="thin-title">{{ Number(data.item.hyperParameters.batchSizeFraction) || 'N/A' }}</h2>
              <span class="text-secondary">Epochs</span>
              <h2 class="thin-title">{{ Number(data.item.hyperParameters.epochs) || 'N/A' }}</h2>
              <span class="text-secondary">Dense Units</span>
              <h2 class="thin-title">{{ Number(data.item.hyperParameters.denseUnits) || 'N/A' }}</h2>
            </b-popover>
        </template>
        <template slot="loss" slot-scope="data">
          <span v-show="data.item.loss">{{ data.item.loss ? data.item.loss.toFixed(10) : '--' }}</span>
          <span class="text-muted" v-show="!data.item.loss">--</span>
        </template>
        <template slot="shareStatus" slot-scope="data">
          <span :class="shareStatus(data).class" v-if="data.item.url">
            <fa-icon :icon="shareStatus(data).icon"></fa-icon>
            {{ shareStatus(data).text }}
          </span>
          <span class="text-muted" v-show="!data.item.url">--</span>
          <a
            :href="getCurrentHash"
            v-show="data.item.url && data.item.owner === id"
            @click="shareModel(data)">設定</a>
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            size="sm"
            variant="info"
            :id="`copy-btn-${data.item.id}`"
            @click="onUrlCopy(data.item.id)"
            v-clipboard:copy="modelLink(data.item.url)">
            <fa-icon icon="copy" style="color: white"></fa-icon> 複製連結
          </b-button>
          <b-tooltip
            placement="bottom"
            disabled
            :ref="`tooltip-${data.item.id}`"
            :target="`copy-btn-${data.item.id}`"
          >
            <fa-icon icon="clipboard-check"></fa-icon>
            連結已複製
          </b-tooltip>
          <b-button
            size="sm"
            variant="secondary"
            :disabled="data.item.status === 'training' || (data.item.shared && data.item.owner !== id)"
            @click="onEdit(data.item.name)">
            <fa-icon icon="robot"></fa-icon> 重新訓練
          </b-button>
          <b-button
            size="sm"
            variant="success"
            :disabled="!data.item.url"
            @click.stop="onPredictModalOpen(data.item)">
            <fa-icon icon="eye"></fa-icon> 辨識影像
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            :disabled="data.item.url === undefined || (data.item.shared && data.item.owner !== id)"
            @click.stop="onDelete(data.item.name)">
            <fa-icon icon="trash"></fa-icon> 刪除模型
          </b-button>
        </template>
      </b-table>
    </b-row>

    <b-row align-h="center">
      <b-col cols="6" md="3" align-self="center">
        <b-pagination :total-rows="table.totalRows" :per-page="table.perPage" v-model="table.currentPage" />
      </b-col>
    </b-row>


    <!-- Train new model -->
    <b-modal
      size="lg"
      :title="modalTitle"
      :busy="status.modal.busy"
      v-model="status.modal.show"
      @shown="autoFocus">
      <b-form @submit.prevent.stop="onSubmit" ref="form">
        <b-alert variant="danger" :show="status.modal.hasError">{{ status.modal.errorMessage }}</b-alert>
        <b-tabs>
          <b-tab title="基本設定" :active="!status.modal.onEdit">
            <p>&nbsp;</p>
            <b-form-group label="模型名稱">
              <b-input
                v-model="modal.name"
                placeholder="模型名稱"
                ref="autoFocus"
                :disabled="status.modal.onEdit"
                :state="$v.modal.name.specialChar && modal.name"
                required>
              </b-input>
              <b-form-invalid-feedback>
                名稱不得包含特殊字元
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="分類數量">
              <b-input
                v-model.number="modal.count"
                placeholder="分類數量"
                type="number"
                min="2"
                @change="onCountChange"
                required>
              </b-input>
              <p>&nbsp;</p>
              <b-alert variant="warning" :show="typeof modal.classes[0].datasets[0] === 'string'">
                <fa-icon icon="exclamation-triangle"></fa-icon> 偵測到資料集格式問題。<br>
                請執行 <strong>重新訓練</strong>，系統將會嘗試自動修復異常的資料集。
              </b-alert>
              <b-row class="category-row scrollable-row" ref="categoryRow">
                <b-col cols="12" md="4" v-for="(category, index) in modal.classes" :key="index">
                  <b-card>
                    <b-form-group label="分類名稱">
                      <b-input v-model="category.name" required></b-input>
                    </b-form-group>
                    <b-form-group label="資料集">
                      <multiselect
                        v-model="category.datasets"
                        :options="unselectedDataset"
                        :multiple="true"
                        :max-height="120"
                        :show-labels="false"
                        :hide-selected="true"
                        :custom-label="datasetLabel"
                        track-by="id"
                        deselect-label="移除"
                        placeholder="搜尋或從列表選擇">
                        <span slot="noResult">找不到結果 :(</span>
                      </multiselect>
                    </b-form-group>
                    <b-form-group label="標籤">
                      <tags-input
                        v-model.trim="category.tags"
                        wrapper-class="form-control"
                        placeholder="加入標籤"
                        add-tags-on-comma>
                      </tags-input>
                    </b-form-group>
                  </b-card>
                </b-col>
              </b-row>
            </b-form-group>
          </b-tab>
          <b-tab title="超參數設定" :active="status.modal.onEdit">
            <p>&nbsp;</p>
            <b-row>
              <b-col cols="12" md="3">
                <b-form-group label="Learning Rate">
                  <b-input v-model.number="modal.hyperParameters.learningRate" type="number" required></b-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Batch Size Fraction">
                  <b-input v-model.number="modal.hyperParameters.batchSizeFraction" type="number" required></b-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Epochs">
                  <b-input v-model.number="modal.hyperParameters.epochs" type="number" required></b-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Dense Units">
                  <b-input v-model.number="modal.hyperParameters.denseUnits" type="number" required></b-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-form>

      <div slot="modal-footer" style="width: 100%">
        <b-row>
          <p class="text-info ml-3 mb-0">
            <fa-icon icon="info-circle"></fa-icon> 訓練任務成功啟動後才會顯示於模型清單中
          </p>
          <div class="d-lg-none" style="width: 100%"></div>
          <b-col align-self="end" style="text-align: right">
            <b-btn variant="secondary" @click="status.modal.show = false">取消</b-btn>
            <b-btn
              variant="success"
              @click="onModalOk"
              :disabled="$v.modal.name.$invalid || $v.modal.classes.$invalid || modal.status === 'training'">
              {{ status.modal.onEdit ? '重新訓練' : '開始訓練' }}
            </b-btn>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <!-- /Train new model -->

    <!-- Predict modal -->
    <b-modal
      size="lg"
      title="影像辨識"
      v-model="status.predict.show"
      @hidden="onPredictModalHidden">
      <b-card header-tag="header">
        <b slot="header">
          <fa-icon icon="info-circle"></fa-icon>
          模型資訊
        </b>
        <b-row>
          <b-col>
            <p class="text-secondary">模型名稱</p>
            <h3 class="thin-title">{{ predictModal.model.name }}</h3>
          </b-col>
          <b-col>
            <p class="text-secondary">類別數量</p>
            <h3 class="thin-title">{{ predictModal.model.classes.length }} 個類別</h3>
          </b-col>
        </b-row>
        <br>
        <p class="text-secondary">超參數</p>
        <b-row class="scrollable-row" ref="hyperParameterRow">
          <b-col cols="10" md="4">
            <b-card>
              <p class="ellipsis-text">Batch Size Fraction</p>
              <h3 class="thin-title">
                {{ predictModal.model.hyperParameters ? predictModal.model.hyperParameters.batchSizeFraction : 'N/A' }}
              </h3>
            </b-card>
          </b-col>
          <b-col cols="10" md="4">
            <b-card>
              <p class="ellipsis-text">Dense Units</p>
              <h3 class="thin-title">
                {{ predictModal.model.hyperParameters ? predictModal.model.hyperParameters.denseUnits : 'N/A' }}
              </h3>
            </b-card>
          </b-col>
          <b-col cols="10" md="4">
            <b-card>
              <p class="ellipsis-text">Epochs</p>
              <h3 class="thin-title">
                {{ predictModal.model.hyperParameters ? predictModal.model.hyperParameters.epochs : 'N/A' }}
              </h3>
            </b-card>
          </b-col>
          <b-col cols="10" md="4">
            <b-card>
              <p class="ellipsis-text">Learning Rate</p>
              <h3 class="thin-title">
                {{ predictModal.model.hyperParameters ? predictModal.model.hyperParameters.learningRate : 'N/A' }}
              </h3>
            </b-card>
          </b-col>
        </b-row>
      </b-card>
      <br>
      <b-card no-body>
        <b-tabs v-model="status.predict.mode" @input="predictionModalSwitch" card>
          <b-tab active>
            <template slot="title">
              <fa-icon icon="images"></fa-icon>
              辨識影像檔
            </template>
            <br>
            <b-alert variant="danger" :show="status.predict.image.hasError">
              {{ status.predict.image.errorMessage }}
            </b-alert>
            <b-file type="file" accept="image/*" @change="loadImageFromFile" ref="fileinput"></b-file><br>
            <p>&nbsp;</p>
            <b-row v-if="status.predict.image.predicting">
              <b-col cols="12" md="6" class="text-center">
                <b-img
                  :src="predictModal.imgSrc"
                  :style="{
                    width: 'auto',
                    height: 'auto',
                    'max-width': '224px',
                    'max-height': '224px',
                    transform: 'rotate(' + status.predict.image.rotate + 'deg)'
                  }"
                  @load="prediction"
                  ref="fileImage"></b-img>
                <b-row align-h="between" class="mt-2" style="width: 224px; margin: 0 auto;">
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.image.result.class > -1"
                      @click="rotatePreview('image', -90)">
                      <fa-icon icon="undo"></fa-icon>
                      旋轉
                    </b-btn>
                  </div>
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.image.result.class > -1"
                      @click="rotatePreview('image', 90)">
                      旋轉
                      <fa-icon icon="undo" transform="flip-h"></fa-icon>
                    </b-btn>
                  </div>
                </b-row>
                <p>&nbsp;</p>
              </b-col>
              <b-col cols="12" md="6">
                <p class="text-secondary">辨識結果</p>
                <h2 class="thin-title">
                  {{ predictionResult }}
                  <fa-icon icon="spinner" spin v-if="status.predict.image.result.class < 0"></fa-icon>
                </h2>
                <p class="text-secondary">信心度</p>
                <h2 class="thin-title">
                  {{
                    status.predict.image.result.confidence ?
                      `${Math.round(status.predict.image.result.confidence * 10000) / 100} %` : '--'
                  }}
                </h2>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab>
            <template slot="title">
              <fa-icon icon="video"></fa-icon>
              辨識 Webcam 影像
            </template>
            <br>
            <b-row>
              <b-col
                class="text-center"
                style="margin-top: auto; margin-bottom: auto;">
                <video
                  autoplay
                  playsinline
                  muted
                  :style="{
                    width: '224px',
                    height: '224px',
                    transform: 'rotate(' + status.predict.webcam.rotate + 'deg)'
                  }"
                  poster="../assets/poster-waiting.png"
                  ref="video"></video>
                <b-row align-h="between" class="mt-2" style="width: 224px; margin: 0 auto;">
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.webcam.result.class > -1"
                      @click="rotatePreview('webcam', -90)">
                      <fa-icon icon="undo"></fa-icon>
                      旋轉
                    </b-btn>
                  </div>
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.webcam.result.class > -1"
                      @click="rotatePreview('webcam', 90)">
                      旋轉
                      <fa-icon icon="undo" transform="flip-h"></fa-icon>
                    </b-btn>
                  </div>
                </b-row>
              </b-col>
              <b-col>
                <b-button
                  block
                  :variant="webcamPredictionButton.variant"
                  @click="predictWebcam">
                  {{ webcamPredictionButton.text }}
                </b-button>
                <p>&nbsp;</p>
                <b-alert variant="danger" :show="status.predict.webcam.hasError">{{ status.predict.webcam.errorMessage }}</b-alert>
                <div v-if="status.predict.webcam.predicting">
                  <p class="text-secondary">辨識結果</p>
                  <h2 class="thin-title">
                    {{ predictionResult }}
                    <fa-icon icon="spinner" spin v-if="status.predict.webcam.result.class < 0"></fa-icon>
                  </h2>
                  <p class="text-secondary">信心度</p>
                  <h2 class="thin-title">
                    {{
                      status.predict.webcam.result.confidence ?
                        `${Math.round(status.predict.webcam.result.confidence * 10000) / 100} %` : '--'
                    }}
                  </h2>
                </div>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab>
            <template slot="title">
              <fa-icon icon="link"></fa-icon>
              辨識網路影像
            </template>
            <br>
            <b-alert variant="danger" :show="status.predict.url.hasError">
              {{ status.predict.url.errorMessage }}
            </b-alert>
            <label>欲辨識的影像連結</label>
            <b-input-group>
              <b-input
              v-model="status.predict.url.link"
              :disabled="status.predict.url.loading"></b-input>
              <b-input-group-append>
                <b-button
                :variant="urlPredictionButton.variant"
                @click="predictionButtonHandler">
                  {{ urlPredictionButton.text }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-text>輸入以 http:// 或 https:// 開頭的網址</b-form-text>
            <p>&nbsp;</p>
            <b-row v-if="status.predict.url.loading">
              <b-col cols="12" md="6" class="text-center">
                <b-img
                  :src="predictModal.imgSrc"
                  :style="{
                    width: 'auto',
                    height: 'auto',
                    'max-width': '224px',
                    'max-height': '224px',
                    transform: 'rotate(' + status.predict.url.rotate + 'deg)'
                  }"
                  @load="prediction"
                  ref="urlImage"></b-img>
                <b-row align-h="between" class="mt-2" style="width: 224px; margin: 0 auto;">
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.url.result.class > -1"
                      @click="rotatePreview('url', -90)">
                      <fa-icon icon="undo"></fa-icon>
                      旋轉
                    </b-btn>
                  </div>
                  <div class="px-0">
                    <b-btn
                      variant="outline-info"
                      v-if="this.status.predict.url.result.class > -1"
                      @click="rotatePreview('url', 90)">
                      旋轉
                      <fa-icon icon="undo" transform="flip-h"></fa-icon>
                    </b-btn>
                  </div>
                </b-row>
                <p>&nbsp;</p>
              </b-col>
              <b-col cols="12" md="6">
                <p class="text-secondary">辨識結果</p>
                <h2 class="thin-title">
                  {{ predictionResult }}
                  <fa-icon icon="spinner" spin v-if="status.predict.url.result.class < 0"></fa-icon>
                </h2>
                <p class="text-secondary">信心度</p>
                <h2 class="thin-title">
                  {{
                    status.predict.url.result.confidence ?
                      `${Math.round(status.predict.url.result.confidence * 10000) / 100} %` : '--'
                  }}
                </h2>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-card>

      <div slot="modal-footer">
        <b-btn variant="secondary" @click="status.predict.show = false">關閉</b-btn>
      </div>
    </b-modal>
    <!-- /Predict modal -->

  </b-container>
</template>

<script>
import axios from 'axios';
import multiselect from 'vue-multiselect';
import * as tf from '@tensorflow/tfjs';
import Vue from 'vue';
import io from 'socket.io-client';
import { validationMixin } from 'vuelidate';
import { required, minLength, helpers } from 'vuelidate/lib/validators';
import customMixin from '../lib/mixin.js';
import imageUtility from '../lib/image-utility.js';
import eventBus from '../lib/event-bus.js';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { setInterval, clearInterval } from 'timers';

export default {
  components: { multiselect },
  mixins: [validationMixin, customMixin],
  data () {
    return {
      id: this.decodeHash(location.hash),
      dataset: [],
      model: [],
      constant: {
        host: 'https://imageml2.webduino.io',
        strings: {
          emptyFilteredText: '找不到指定紀錄',
          emptyText: '沒有任何紀錄可顯示'
        },
        error: {
          save: '儲存模型時發生錯誤，請稍後再試',
          predict: '辨識影像時發生未知錯誤',
          noPermission: '無法取得 Webcam 影像',
          loadImage: '載入影像時發生錯誤，請確定連結是否正確',
          train: {
            UNKNOWN: '發生未知錯誤',
            E_NO_TRAINING_DATA: '訓練資料有誤',
            E_BATCH_SIZE: 'Batch 數值有誤',
            E_NO_TRAINING_EXAMPLE: '沒有訓練樣本'
          }
        }
      },
      table: {
        items: [],
        fields: [
          { key: 'name', label: '名稱', sortable: true, sortDirection: 'asc', class: 'text-center' },
          { key: 'classCount', label: '分類數量', class: 'text-center' },
          { key: 'loss', label: '損失誤差', sortable: true, class: 'text-center' },
          { key: 'progress', label: '訓練進度', class: 'text-center' },
          { key: 'shareStatus', label: '分享狀態', class: 'text-center' },
          { key: 'actions', label: '操作' }
        ],
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        filter: null,
        onlyMe: true
      },
      modal: {
        name: '',
        count: 2,
        classes: [
          { name: '', datasets: [], tags: [] },
          { name: '', datasets: [], tags: [] }
        ],
        hyperParameters: {
          learningRate: 0.0002,
          batchSizeFraction: 0.2,
          epochs: 50,
          denseUnits: 100
        }
      },
      predictModal: {
        tensorflow: {
          mobilenet: null,
          secondLayer: null
        },
        model: {
          classes: []
        },
        imgSrc: ''
      },
      tooltips: {},
      status: {
        refreshing: false,
        privateDataset: true,
        modal: {
          animated: false,
          show: false,
          busy: false,
          onEdit: false,
          hasError: false,
          errorMessage: ''
        },
        predict: {
          show: false,
          mode: 0,
          image: {
            hasError: false,
            errorMessage: '',
            rotate: 0,
            predicting: false,
            result: {
              class: -1,
              confidence: null,
            }
          },
          webcam: {
            hasError: false,
            errorMessage: '',
            interval: null,
            rotate: 0,
            predicting: false,
            result: {
              class: -1,
              confidence: null,
            }
          },
          url: {
            hasError: false,
            errorMessage: '',
            link: '',
            rotate: 0,
            predicting: false,
            loading: false,
            result: {
              class: -1,
              confidence: null,
            }
          }
        }
      }
    }
  },
  validations: {
    modal: {
      name: {
        required,
        specialChar: name => !/[\/\?\#]/.test(name)
      },
      classes: {
        $each: {
          name: {
            required,
          },
          datasets: {
            required,
            minLength: minLength(1)
          }
        }
      }
    }
  },
  methods: {
    async loadTableData () {
      this.status.refreshing = true;
      const timer = new Promise((resolve, reject) => {
        setTimeout(resolve, 1000);
      });
      const getModel = axios(`${this.constant.host}/mlapi/models?hashkey=${this.encodeHash()}`);
      const response = await Promise.all([timer, getModel]);
      const items = response[1].data;
      
      this.table.totalRows = items.length;
      this.$data.table.items = items;
      this.status.refreshing = false;
    },
    async loadDataset () {
      const datasetResponse = await axios(`${this.constant.host}/mlapi/datasets?hashkey=${this.encodeHash()}`);
      this.dataset = datasetResponse.data;
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    async onOpen () {
      const env = this.getResponsiveBreakpoint();
      const animationTrigger = env === 'xs' ? 2 : 4;

      await this.loadDataset();
      this.status.modal.onEdit = false;
      this.status.modal.show = true;
      this.status.modal.hasError = false;
      this.status.modal.animated = false;
      this.$data.modal = {
        name: '',
        count: 2,
        classes: [
          { name: '', datasets: [], tags: [] },
          { name: '', datasets: [], tags: [] }
        ],
        hyperParameters: {
          learningRate: 0.0002,
          batchSizeFraction: 0.2,
          epochs: 50,
          denseUnits: 100
        }
      };

      if (this.modal.count >= animationTrigger) {
        setTimeout(() => {
          this.scrollLeft(this.$refs.categoryRow, 150, 500);
        }, 700);
        setTimeout(() => {
          this.scrollLeft(this.$refs.categoryRow, 0, 500);
          this.status.modal.animated = true;
        }, 1200);
      }
    },
    async onEdit (name) {
      this.status.modal.onEdit = true;
      const response = await axios(`${this.constant.host}/mlapi/models?hashkey=${this.encodeHash()}`);
      const models = response.data.filter(model => model.name === name);
      
      this.$data.modal = models[0];
      this.$data.modal.count = this.$data.modal.classes.length;
      this.status.modal.show = true;
    },
    autoFocus () {
      const env = this.getResponsiveBreakpoint();
      if (env !== 'xs') {
        this.$refs.autoFocus.focus();
      }
    },
    async onModalOk () {
      await this.onSubmit();
    },
    shareStatus (data) {
      const publicTemplate = { text: '公開分享', class: '', icon: 'users' };
      const privateTemplate = { text: '私人檔案', class: 'text-muted', icon: 'lock' };
      
      if (data.item.owner !== this.id) {
        publicTemplate.text = `由用戶 ${data.item.owner} 分享`;
      }
      return data.item.shared ? publicTemplate : privateTemplate;
    },
    shareLinkText (data) {
      return data.item.shared ? '取消分享' : '公開分享';
    },
    async shareModel (data) {
      const content = document.createElement('p');

      content.innerHTML =
        `${data.item.name} 目前為<b>${this.shareStatus(data).text}</b>，確定要${this.shareLinkText(data)}嗎？`;
      const confirm = await this.$swal({
        title: `正在${this.shareLinkText(data)}`,
        content,
        buttons: {
          cancel: {
            text: '取消',
            visible: true,
            closeModal: true
          },
          confirm: { text: this.shareLinkText(data), closeModal: false }
        }
      });
      if (confirm) {
        const name = encodeURIComponent(data.item.name);
        const shareStatus = !data.item.shared;
  
        try {
          await axios.put(`${this.constant.host}/mlapi/model/${name}/shared/${shareStatus}?hashkey=${this.encodeHash()}`);
          await this.loadTableData();
          swal({
            icon: 'success',
            text: `太好了！已將 ${data.item.name} 設定為${this.shareLinkText(data)}`
          });
        } catch (ex) {
          console.error(ex);
          swal({
            icon: 'danger',
            text: `分享失敗，請再試一次`
          });
        }
      }
    },
    modelLink (url) {
      if (url) {
        return `/${url.match('/ml_models/(.*)/model.json')[1]}`;
      } else {
        return '';
      }
    },
    onUrlCopy (id) {
      if (this.$refs[`tooltip-${id}`]) {
        this.$refs[`tooltip-${id}`].$emit('open');
        setTimeout(() => {
          this.$refs[`tooltip-${id}`].$emit('close');
        }, 2000);
      }
    },
    async onSubmit () {
      const modalData = Object.assign({}, this.modal);

      delete modalData.count;
      try {
        await axios.post(
          `${this.constant.host}/mlapi/train/${encodeURI(this.modal.name)}?hashkey=${this.encodeHash()}`,
          modalData
        );
        this.status.modal.show = false;
        await this.loadTableData();
      } catch (ex) {
        this.status.modal.hasError = true;
        this.status.modal.errorMessage = this.constant.error.train.UNKNOWN;
        console.error(ex);
      }
    },
    onCountChange () {
      const currentCount = this.modal.classes.length;
      const diff = this.modal.count - currentCount;
      const env = this.getResponsiveBreakpoint();
      const animationTrigger = env === 'xs' ? 2 : 4;
      let earlyReturn = false;

      if (!this.modal.count || this.modal.count < 2) {
        this.modal.count = 2;
        this.$data.modal.classes = [
          { name: '', datasets: [], tags: [] },
          { name: '', datasets: [], tags: [] }
        ];
        earlyReturn = true;
      }

      if (!this.status.modal.animated) {
        if (this.modal.count >= animationTrigger) {
          setTimeout(() => {
            this.scrollLeft(this.$refs.categoryRow, 200, 500);
          }, 300);
          setTimeout(() => {
            this.scrollLeft(this.$refs.categoryRow, 0, 500);
            this.status.modal.animated = true;
          }, 800);
        }
      }

      if (earlyReturn) {
        return;
      }
      
      if (diff === 0) {
        return;
      }

      if (diff > 0) {
        const classArray = [];
        for (let i = 0; i < diff; i++) {
          classArray.push({
            name: '',
            datasets: []
          });
        }
        this.modal.classes = this.modal.classes.concat(classArray);
      }

      if (diff < 0) {
        this.$data.modal.classes.splice((currentCount + diff), -diff);
      }
    },
    async onDelete (name) {
      const hiddenInput = document.createElement('input').setAttribute('type', 'hidden');
      const confirm = await this.$swal({
        text: `確定刪除 ${name}？`,
        dangerMode: true,
        content: hiddenInput,
        buttons: {
          cancel: {
            text: '取消',
            visible: true,
            closeModal: true
          },
          confirm: { text: '刪除', closeModal: false }
        }
      });

      if (confirm) {
        try {
          const response = await axios.delete(`${this.constant.host}/mlapi/model/${encodeURI(name)}?hashkey=${this.encodeHash()}`);
          swal({
            icon: 'success',
            text: `已成功刪除 ${name}`
          });
          await this.loadTableData();
        } catch (ex) {
          console.error(ex);
          swal({
            icon: 'error',
            text: `刪除 ${name} 失敗`
          });
        }
      }
    },
    getModelStatus (model) {
      if (model.url) {
        return '已儲存';
      }
      if (model.status === 'training') {
        return '訓練中';
      }
      if (model.status === 'unsaved') {
        return '訓練完成 (未存檔)';
      }
    },
    onPredictModalOpen (model) {
      this.predictModal.model = model;
      this.status.predict.show = true;
      setTimeout(() => {
        this.scrollLeft(this.$refs.hyperParameterRow, 100, 500);
      }, 500);
      setTimeout(() => {
        this.scrollLeft(this.$refs.hyperParameterRow, 0, 500);
      }, 1000);
    },
    onPredictModalHidden () {
      const tabs = ['image', 'webcam', 'url'];

      this.resetPredictionModal();
      tabs.forEach(tab => {
        const tabObject = this.status.predict[tab];
        tabObject.rotate = 0;
        tabObject.hasError = false;
        tabObject.predicting = false;
        tabObject.result = {
          class: -1,
          confidence: null
        };
      });
      this.predictModal.tensorflow.secondLayer = null;
      this.$refs.fileinput.reset();
    },
    predictionModalSwitch (index) {
      this.resetPredictionModal();
    },
    resetPredictionModal () {
      const tabs = ['webcam', 'image', 'url'];
      this.stopWebcamPrediction();

      tabs.forEach(tab => {
        this.status.predict[tab].result = {
          class: -1,
          confidence: null
        };
        this.status.predict[tab].predicting = false;
      });
      this.status.predict.url.loading = false;
    },
    stopWebcamPrediction () {
      const video = this.$refs.video;

      if (video.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.load();
      }
      clearInterval(this.status.predict.webcam.interval);
      this.status.predict.webcam.interval = null;
      this.status.predict.webcam.predicting = false;
    },
    async rotatePreview (type, degree) {
      const currentDegree = this.status.predict[type].rotate;
      let newDegree = 0;

      if (degree < 0) {
        if (currentDegree <= -270) {
          newDegree = 0;
        } else {
          newDegree = currentDegree + degree;
        }
      } else {
        if (currentDegree >= 270) {
          newDegree = 0;
        } else {
          newDegree = currentDegree + degree;
        }
      }
      this.status.predict[type].rotate = newDegree;
      const imageElement = this.getPredictionInfo().element;
      imageElement.dispatchEvent(new Event('load'));
    },
    async loadImageFromFile (event) {
      try {
        this.status.predict.image.hasError = false;
        if (event.target.files[0]) {
          let img = new Image();
          await new Promise(async resolve => {
            img.onload = resolve;
            img.src = await imageUtility.correctOrientation(event.target.files[0]);
          });
          this.status.predict.image.hasError = false;
          this.status.predict.image.predicting = true;
          this.status.predict.image.result.class = -1;
          this.status.predict.image.result.confidence = null;
          this.predictModal.imgSrc = imageUtility.crop(img);
          img = null;
        }
      } catch (ex) {
        this.status.predict.image.hasError = true;
        this.status.predict.image.errorMessage = this.constant.error.predict;
        console.error(ex);
      }
    },
    async loadImageFromUrl () {
      try {
        this.status.predict.url.hasError = false;

        if (!this.status.predict.url.loading) {
          return;
        }

        let img = await imageUtility.load(this.status.predict.url.link);
        img = imageUtility.crop(img);

        if (this.predictModal.imgSrc !== img) {
          /* Only load image when it's updated */
          this.predictModal.imgSrc = img;
        }

        setTimeout(async () => {
          await this.loadImageFromUrl();
        }, 800);
      } catch (ex) {
        console.error(ex);
        this.status.predict.url.hasError = true;
        this.status.predict.url.errorMessage = this.constant.error.loadImage;
      }
    },
    async predictionButtonHandler () {
      const isLoading = this.status.predict.url.loading;
      this.status.predict.url.loading = !isLoading;

      if (this.status.predict.url.loading) {
        await this.loadImageFromUrl();
      }
    },
    async getStream () {
      const video = this.$refs.video;

      this.status.predict.webcam.result.class = -3;
      video.srcObject = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 224,
          height: 224,
          facingMode: 'environment'
        }
      });
      video.play();
      return new Promise((resolve, reject) => {
        video.addEventListener('loadeddata', resolve, { once: true });
      });
    },
    async initTensorflow () {
      const mn = await tf.loadModel(`${this.constant.host}/mobilenet/v1_0.25_224/model.json`);
      const layer = mn.getLayer('conv_pw_13_relu');

      this.predictModal.tensorflow.mobilenet = tf.model({
        inputs: mn.inputs,
        outputs: layer.output
      });
    },
    async loadSecondLayer () {
      this.predictModal.tensorflow.secondLayer = await tf.loadModel(this.predictModal.model.url);
    },
    async predictWebcam () {
      if (this.status.predict.webcam.predicting) {
        this.stopWebcamPrediction();
      } else {
        this.status.predict.webcam.hasError = false;
        this.status.predict.webcam.predicting = true;
        try {
          await this.getStream();
        } catch (ex) {
          console.error(ex);
          this.status.predict.webcam.hasError = true;
          this.status.predict.webcam.errorMessage = this.constant.error.noPermission;
          return;
        }
        await this.prediction();
        this.status.predict.webcam.interval = setInterval(async () => {
          await this.prediction();
        }, 1000);
      }
    },
    getPredictionInfo () {
      let mode = '';
      let element;

      switch (this.status.predict.mode) {
        case 0:
          mode = 'image';
          element = this.$refs.fileImage;
          break;
        case 1:
          mode = 'webcam';
          element = this.$refs.video;
          break;
        case 2:
          mode = 'url';
          element = this.$refs.urlImage;
          break;
      }
      const rotate = this.status.predict[mode].rotate;

      return {
        mode, element, rotate
      };
    },
    async prediction () {
      const mode = this.getPredictionInfo().mode;

      this.status.predict[mode].predicting = true;
      try {
        if (!this.predictModal.tensorflow.mobilenet) {
          this.status.predict[mode].result.class = -2;
          await this.initTensorflow();
        }
        if (!this.predictModal.tensorflow.secondLayer) {
          await this.loadSecondLayer();
        }
        /* Wait for rotating applied or DOM updated */
        await Vue.nextTick();
        const element = imageUtility.rotate(
          this.getPredictionInfo().element,
          this.getPredictionInfo().rotate
        );
        const resultTensor = tf.tidy(() => {
          const mobilenet = this.predictModal.tensorflow.mobilenet;
          const secondmodel = this.predictModal.tensorflow.secondLayer;
          const uploadImage = tf.fromPixels(element);
          const batchedImage = uploadImage.expandDims(0);
          const img = batchedImage.toFloat().div(tf.scalar(127)).sub(tf.scalar(1));
          const activation = mobilenet.predict(img).flatten().expandDims(0);
          const predictions = secondmodel.predict(activation);
          return predictions.as1D();
        });
        let classTensor = resultTensor.argMax();
        let confidenceTensor = resultTensor.max();
        let result = {
          class: (await classTensor.data())[0],
          confidence: (await confidenceTensor.data())[0]
        }
        classTensor.dispose();
        confidenceTensor.dispose();
        resultTensor.dispose();
        this.status.predict[mode].result = result;
      } catch (ex) {
        this.status.predict[mode].errorMessage = this.constant.error.predict;
        this.status.predict[mode].hasError = true;
        this.status.predict[mode].predicting = false;
        this.status.predict[mode].result = {
          class: -1,
          confidence: null
        };
        console.error(ex);
      }
    },
    scrollLeft (element, to, duration) {
      const start = element.scrollLeft;
      const change = to - start;
      const startDate = +new Date();
      const easeInOutQuad = (t, b, c, d) => {
        t /= d/2;
        if (t < 1) return c/2*t*t + b;
        t--;
        return -c/2 * (t*(t-2) - 1) + b;
      };
      const animateScroll = () => {
        const currentDate = +new Date();
        const currentTime = currentDate - startDate;
        element.scrollLeft = parseInt(easeInOutQuad(currentTime, start, change, duration));
        if (currentTime < duration) {
          requestAnimationFrame(animateScroll);
        } else {
          element.scrollLeft = to;
        }
      };
      animateScroll();
    },
    getResponsiveBreakpoint () {
      const envs = { xs: 'd-none', sm: 'd-sm-none', md: 'd-md-none', lg: 'd-lg-none', xl: 'd-xl-none' };
      const $el = document.createElement('div');
      let env = '';

      document.body.appendChild($el);
      for (var i = Object.keys(envs).length - 1; i >= 0; i--) {
        env = Object.keys(envs)[i];
        $el.classList.add(envs[env]);
        // Ref: https://stackoverflow.com/a/21696585/1348494
        if ($el.offsetParent === null) {
          break; // env detected
        }
      }
      $el.parentNode.removeChild($el);
      return env;
    },
    datasetLabel (category) {
      const owner = category.owner === this.id ? '我' : category.owner;
      const ownerInfo = category.owner ? `擁有者：${owner}` : '';

      return `${category.name || '--'} ${ownerInfo}`;
    },
  },
  computed: {
    getCurrentHash () {
      return location.hash;
    },
    tableItem () {
      let items = [];

      if (this.table.onlyMe) {
        items = this.table.items.filter(item => item.owner === this.id);
      } else {
        items = this.table.items;
      }
      this.table.totalRows = items.length;
      return items;
    },
    modalTitle () {
      return this.status.modal.onEdit ? '重新訓練' : '新增模型';
    },
    unselectedDataset () {
      /* Get array of selected dateset ID */
      const selected = [].concat.apply([], this.modal.classes.map(d => d.datasets)).map(d => d.id);
      let items = [];

      /* Determine to show private or all model by current view mode of dataset component  */
      if (this.status.privateDataset) {
        items = this.dataset.filter(item => item.owner === this.id);
      } else {
        items = this.dataset;
      }

      /* Filter out selected dateset, return unselected datasets */
      return items.filter(dataset => selected.indexOf(dataset.id) < 0);
    },
    predictionResult () {
      const mode = this.getPredictionInfo().mode;
      const classIndex = this.status.predict[mode].result.class;
      let result = '';

      switch (classIndex) {
        case -3:
          result = '正在取得攝影機權限 ';
          break;
        case -2:
          result = '正在載入模型 ';
          break;
        case -1:
          result = '辨識中 ';
          break;
        default:
          result = this.predictModal.model.classes[classIndex].name;
          break;
      }
      return result;
    },
    urlPredictionButton () {
      if (this.status.predict.url.loading) {
        return {
          variant: 'danger',
          text: '停止辨識'
        }
      } else {
        return {
          variant: 'info',
          text: '辨識影像'
        }
      }
    },
    webcamPredictionButton () {
      const isWebcamPredicting = this.status.predict.webcam.predicting;
      return {
        variant: isWebcamPredicting ? 'outline-success' : 'success',
        text: isWebcamPredicting ? '停止辨識' : '開始辨識'
      }
    }
  },
  async created () {
    const socket = io(`${this.constant.host}/?hashkey=${this.encodeHash()}`);

    await Promise.all([
      this.loadDataset(),
      this.loadTableData()
    ]);

    setInterval(async () => {
      await Promise.all([
        this.loadDataset(),
        this.loadTableData()
      ]);
    }, 10000);

    socket.on('job_error', data => {
      const errorMessage = this.constant.error.train[data.error.code] ||
        this.constant.error.train.UNKNOWN;
      
      this.makeToast({
        content: 
        `訓練模型 ${data.name} 時發生錯誤：${errorMessage}。請確認設定無誤後再嘗試一次。`
      });
    });
    
    eventBus.$on('isPrivateDataset', onlyMe => {
      this.status.privateDataset = onlyMe;
    });
  },
  beforeDestroy () {
    eventBus.$off('isPrivateDataset');
  }
}
</script>

<style lang="scss" scoped>
  .thin-title {
    font-weight: 200;
  }
  .card-img-top {
    width: 224px;
    height: 224px;
  }
  .scrollable-row {
    overflow-x: scroll;
    flex-wrap: nowrap;
  }
  .ellipsis-text {
    width: 100%;
    word-break: break-all;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
</style>
